import React, { FC } from "react";
import { GitPullRequestArrow } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "./ui/input";
import InlineHint from "../features/shared/InlineHint";
import { Spinner } from "./ui/spinner";

interface JsonEditorActionsProps {
  onSave: (e: React.MouseEvent, commitMessage: string) => void;
  onPublish: (e: React.MouseEvent) => void;
  hasCommits: boolean;
  isBusy?: boolean;
  isDirty?: boolean;
  resultCount?: number;
  activeBranch?: string;
  defaultBranch?: string;
}

export const JsonEditorActions: FC<JsonEditorActionsProps> = ({
  onSave,
  onPublish,
  hasCommits,
  isBusy = false,
  isDirty = false,
  resultCount = 0,
  activeBranch,
  defaultBranch,
}) => {
  const [commitMessage, setCommitMessage] = React.useState(
    "Update file content"
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCommitMessage(e.target.value);
  };

  const handleSaveClick = (e: React.MouseEvent) => {
    onSave(e, commitMessage);
  };
  const showPullRequest = Boolean(activeBranch) && activeBranch !== defaultBranch;

  return (
    <div className="sticky bottom-2 z-20 mt-4 flex w-full items-center justify-between rounded border border-gray-200 bg-white/90 p-4 shadow-sm backdrop-blur-sm">
      <div className="space-y-1">
        <p className="text-sm font-medium">
          {isDirty ? "Unsaved changes detected" : "All changes are saved in current editor session"}
        </p>
        <p className="text-xs text-muted-foreground">
          Visible rows: {resultCount}. Save creates a commit in working branch. Pull request targets default branch.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Dialog>
          <DialogTrigger asChild>
            <Button disabled={isBusy || !isDirty}>Save</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save all changes</DialogTitle>
              <DialogDescription>
                  This action will commit to GitHub with all the changes you
                  made to the working (not master) branch.
                Below you can change the name of the commit
              </DialogDescription>
            </DialogHeader>
            <Input value={commitMessage} onChange={handleInputChange} />
            <Button onClick={handleSaveClick} disabled={isBusy || !commitMessage.trim()}>
              {isBusy ? <Spinner className="mr-1 h-4 w-4" /> : null}
              Save
            </Button>
          </DialogContent>
        </Dialog>
        {showPullRequest ? (
          <Button onClick={onPublish} disabled={!hasCommits || isBusy || isDirty} variant="secondary">
            {isBusy ? <Spinner className="mr-1 h-4 w-4" /> : <GitPullRequestArrow className="mr-1 h-4 w-4" />}
            Pull request
          </Button>
        ) : null}
        <InlineHint text="Save commits to the working branch. Pull request is available only for non-default branches and opens PR to default branch." />
      </div>
    </div>
  );
};
